'use client';

import * as React from 'react';
import { useCollection, useFirestore } from '@/firebase';
import { getAlbumCache, parseHybridMediaJson } from '@/lib/gallery-cache';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Query,
  DocumentData,
  Timestamp,
  doc,
  getDoc,
  getDocs
} from 'firebase/firestore';
import { MediaItem, getVideoEmbedUrl, getVideoThumbnailUrl } from '@/models/media-item';
import { Album, normalizeAlbumPath } from '@/models/album';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@/components/ui/visually-hidden';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DriveThumbnail } from '@/components/ui/drive-thumbnail';
import { EditMediaDialog } from './edit-media-dialog';
import { EditAlbumDialog } from './edit-album-dialog';
import { MoveCopyMediaDialog } from './move-copy-media-dialog';
import { QuickTagAircraftDialog } from './quick-tag-aircraft-dialog';
import { deleteMediaItem } from '@/lib/gallery-actions';
import { useAuth } from '@/firebase';
import { useUserData } from '@/hooks/use-user-data';
import { BatchMoveDialog } from './batch-move-dialog';
import { CreateAlbumDialog } from './create-album-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ChevronLeft,
  ChevronRight,
  Grid3x3,
  List,
  LayoutGrid,
  Loader2,
  Play,
  Calendar,
  User,
  Eye,
  X,
  Folder,
  MoreVertical,
  Pencil,
  Trash,
  Settings,
  Move,
  Copy,
  Plane,
  Upload,
  RotateCw,
  RotateCcw,
  Maximize,
  Minimize,
  Share2
} from 'lucide-react';
import Image from 'next/image';
import { formatDistanceToNow } from 'date-fns';
import { getHybridAlbumMedia, getHybridSubAlbums, enrichAlbumsWithCovers, type HybridMediaItem, type HybridAlbum } from '@/lib/gallery-hybrid';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import dynamic from 'next/dynamic';

const PanoramaViewer = dynamic(
  () => import('./panorama-viewer').then(mod => ({ default: mod.PanoramaViewer })),
  {
    ssr: false,
    loading: () => (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    ),
  }
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut' as const
    }
  }
};

export type ViewMode = 'grid' | 'masonry' | 'list';
export type SortBy = 'newest' | 'oldest' | 'popular' | 'title';

export interface GalleryBrowserProps {
  /** Album to display media from (includes sub-albums if albumPath used) */
  albumId?: string;
  /** Album source (firebase) */
  albumSource?: 'firebase';
  /** Filter by media type */
  mediaType?: 'photo' | 'video';
  /** Filter by uploader */
  uploadedBy?: string;
  /** Filter by date range */
  dateRange?: { start: Date; end: Date };
  /** Filter by tags */
  tags?: string[];
  /** Filter by aircraft model name */
  aircraftModelFilter?: string;
  /** View mode */
  viewMode?: ViewMode;
  /** Sort order */
  sortBy?: SortBy;
  /** Number of items per page */
  pageSize?: number;
  /** Show filters UI */
  showFilters?: boolean;
  /** Show view mode selector */
  showViewModeSelector?: boolean;
  /** Callback when media item clicked */
  onMediaClick?: (media: MediaItem | HybridMediaItem) => void;
  /** Callback when album clicked */
  onAlbumClick?: (album: HybridAlbum) => void;
  /** Callback when album is edited/deleted (to refresh sidebar) */
  onAlbumChange?: () => void;
  /** Whether files are being dragged over the gallery */
  isDraggingOver?: boolean;
  /** Disable built-in lightbox */
  disableLightbox?: boolean;
  /** Media ID to auto-open in lightbox (from URL deep link) */
  initialMediaId?: string;
  /** Callback when lightbox media changes (to update URL) */
  onLightboxMediaChange?: (mediaId: string | null) => void;
}

/**
 * Unified gallery browser for displaying photos and videos from Firebase.
 * Supports filtering, sorting, pagination, and multiple view modes.
 */
export function GalleryBrowser({
  albumId,
  albumSource,
  mediaType,
  uploadedBy,
  dateRange,
  tags,
  aircraftModelFilter,
  viewMode: initialViewMode = 'masonry',
  sortBy: initialSortBy = 'newest',
  pageSize = 50,
  showFilters = false,
  showViewModeSelector = true,
  onMediaClick,
  onAlbumClick,
  onAlbumChange,
  isDraggingOver = false,
  disableLightbox = false,
  initialMediaId,
  onLightboxMediaChange,
}: GalleryBrowserProps) {
  const firestore = useFirestore();
  const auth = useAuth();
  const { toast } = useToast();
  const { isAdmin, isOfficer } = useUserData();
  const [viewMode, setViewMode] = React.useState<ViewMode>(initialViewMode);
  const [sortBy, setSortBy] = React.useState<SortBy>(initialSortBy);
  const [selectedMediaIndex, setSelectedMediaIndex] = React.useState<number | null>(null);
  const [albumPath, setAlbumPath] = React.useState<string | null>(null);

  // Album data for edit permissions
  const [currentAlbum, setCurrentAlbum] = React.useState<{
    id: string;
    name: string;
    description?: string;
    sortName?: string | null;
    createdBy?: string;
    mediaCount?: number;
  } | null>(null);
  const [showAlbumEdit, setShowAlbumEdit] = React.useState(false);

  // Edit/Delete state
  const [mediaToEdit, setMediaToEdit] = React.useState<MediaItem | null>(null);
  const [mediaToDelete, setMediaToDelete] = React.useState<MediaItem | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  // Move/Copy state
  const [mediaToMove, setMediaToMove] = React.useState<MediaItem | HybridMediaItem | null>(null);
  const [moveCopyMode, setMoveCopyMode] = React.useState<'move' | 'copy'>('move');

  // Quick Tag Aircraft state
  const [mediaToTagAircraft, setMediaToTagAircraft] = React.useState<MediaItem | HybridMediaItem | null>(null);

  // Rotation state
  const [isRotating, setIsRotating] = React.useState(false);
  const [imageCacheBuster, setImageCacheBuster] = React.useState(0);

  // Lightbox fullscreen state
  const [isPanoFullscreen, setIsPanoFullscreen] = React.useState(false);
  const lightboxContainerRef = React.useRef<HTMLDivElement>(null);
  // Keep panoContainerRef as alias for backward compat
  const panoContainerRef = lightboxContainerRef;

  // Refresh trigger
  const [refreshKey, setRefreshKey] = React.useState(0);

  // For hybrid albums, fetch subalbums and media
  const [hybridSubAlbums, setHybridSubAlbums] = React.useState<HybridAlbum[]>([]);
  const [hybridMedia, setHybridMedia] = React.useState<HybridMediaItem[]>([]);
  const [hybridLoading, setHybridLoading] = React.useState(false);
  const [hybridError, setHybridError] = React.useState<Error | null>(null);

  // Batch Selection State
  // We track items as "type:id" strings to handle mixed selection in a single set if needed,
  // OR we can maintain two sets. Two sets is cleaner for API calls.
  const [selectedAlbumIds, setSelectedAlbumIds] = React.useState<Set<string>>(new Set());
  const [selectedMediaIds, setSelectedMediaIds] = React.useState<Set<string>>(new Set());
  const [showBatchMoveDialog, setShowBatchMoveDialog] = React.useState(false);
  const [showCreateAlbumDialog, setShowCreateAlbumDialog] = React.useState(false);
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = React.useState(false);
  const [isDeletingBatch, setIsDeletingBatch] = React.useState(false);

  const totalSelected = selectedAlbumIds.size + selectedMediaIds.size;
  const isSelectionActive = totalSelected > 0;

  // Check permissions (simple check for now, can be enhanced with role checking)
  const canManage = auth.currentUser !== null;

  const handleEditMedia = (media: MediaItem | HybridMediaItem) => {
    setMediaToEdit(media as MediaItem);
  };

  const handleDeleteClick = (media: MediaItem | HybridMediaItem) => {
    setMediaToDelete(media as MediaItem);
  };

  const handleMoveClick = (media: MediaItem | HybridMediaItem) => {
    setMoveCopyMode('move');
    setMediaToMove(media);
  };

  const handleCopyClick = (media: MediaItem | HybridMediaItem) => {
    setMoveCopyMode('copy');
    setMediaToMove(media);
  };

  const handleTagAircraftClick = (media: MediaItem | HybridMediaItem) => {
    setMediaToTagAircraft(media);
  };

  const handleMoveCopyComplete = () => {
    // Remove from local state if moved (not copied)
    if (moveCopyMode === 'move' && mediaToMove) {
      setHybridMedia(prev => prev.filter(m => m.id !== mediaToMove.id));
      setSelectedMediaIndex(null);
    }
    setMediaToMove(null);
  };

  const handleConfirmDelete = async () => {
    if (!mediaToDelete || !firestore) return;

    setIsDeleting(true);
    try {
      await deleteMediaItem(firestore, mediaToDelete.id);
      toast({
        title: 'Media Deleted',
        description: 'The item has been removed from the gallery.',
      });
      // Close lightbox if open (prevent stale index reference)
      setSelectedMediaIndex(null);
      // Refresh list - simply removing from local state for immediate feedback
      setHybridMedia(prev => prev.filter(m => m.id !== mediaToDelete.id));

      setMediaToDelete(null);
    } catch (error) {
      console.error("Delete failed", error);
      toast({
        variant: "destructive",
        title: "Delete Failed",
        description: "Could not delete the media item."
      });
      // Clear the delete dialog so user can try again or cancel
      setMediaToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMediaUpdate = (mediaId: string, updates: Record<string, any>) => {
    // Update the in-memory media list instead of reloading the page
    setHybridMedia(prev => prev.map(m => {
      if (m.id !== mediaId) return m;
      return {
        ...m,
        title: updates.title ?? m.title,
        description: updates.description ?? m.description,
        tags: updates.tags ?? m.tags,
        aircraftModelIds: updates.aircraftModelIds ?? (m as any).aircraftModelIds,
        aircraftModelNames: updates.aircraftModelNames ?? (m as any).aircraftModelNames,
        isPanorama: updates.isPanorama ?? (m as any).isPanorama,
      } as typeof m;
    }));
    // Also update mediaToEdit so the lightbox reflects changes immediately
    setMediaToEdit(null);
  };

  const handleRotateImage = async (direction: 'cw' | 'ccw') => {
    if (!selectedMedia || !auth.currentUser) return;

    setIsRotating(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('/api/gallery/rotate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ mediaId: selectedMedia.id, direction }),
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to rotate image');
      }

      const data = await response.json();

      // Update local media state with new dimensions
      setHybridMedia(prev => prev.map(m => {
        if (m.id !== selectedMedia.id) return m;
        return { ...m, width: data.width, height: data.height };
      }));

      // Bust image cache so the rotated image loads
      setImageCacheBuster(Date.now());

      toast({
        title: 'Image Rotated',
        description: `Rotated ${direction === 'cw' ? 'clockwise' : 'counter-clockwise'} and thumbnail regenerated.`,
      });
    } catch (error) {
      console.error('Rotate error:', error);
      toast({
        variant: 'destructive',
        title: 'Rotation Failed',
        description: error instanceof Error ? error.message : 'Could not rotate image.',
      });
    } finally {
      setIsRotating(false);
    }
  };

  const togglePanoFullscreen = React.useCallback(() => {
    const el = lightboxContainerRef.current;
    if (!el) return;

    if (!document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  // Sync state when exiting fullscreen via Escape or browser controls
  React.useEffect(() => {
    const handleChange = () => {
      setIsPanoFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const handleAlbumUpdate = async () => {
    // Refetch album data to reflect changes
    if (firestore && albumId) {
      try {
        const albumRef = doc(firestore, 'albums', albumId);
        const albumSnap = await getDoc(albumRef);
        if (albumSnap.exists()) {
          const data = albumSnap.data();
          setCurrentAlbum({
            id: albumId,
            name: data.name || 'Untitled Album',
            description: data.description,
            sortName: data.sortName || null,
            createdBy: data.createdBy,
            mediaCount: data.mediaCount || 0,
          });
        }
      } catch (error) {
        console.error('Error refetching album data:', error);
      }
    }
    // Notify parent to refresh the sidebar tree
    onAlbumChange?.();
  };

  const handleAlbumDelete = () => {
    // Notify parent to refresh the sidebar tree
    onAlbumChange?.();
    // Navigate to parent or gallery root
    window.location.href = '/gallery';
  };

  // Batch Selection Handlers
  const toggleAlbumSelection = (albumId: string) => {
    const newSet = new Set(selectedAlbumIds);
    if (newSet.has(albumId)) {
      newSet.delete(albumId);
    } else {
      newSet.add(albumId);
    }
    setSelectedAlbumIds(newSet);
  };

  const toggleMediaSelection = (mediaId: string) => {
    const newSet = new Set(selectedMediaIds);
    if (newSet.has(mediaId)) {
      newSet.delete(mediaId);
    } else {
      newSet.add(mediaId);
    }
    setSelectedMediaIds(newSet);
  };

  const handleSelectAll = () => {
    if (totalSelected === (subAlbums?.length || 0) + (mediaItems?.length || 0)) {
      // Deselect all
      setSelectedAlbumIds(new Set());
      setSelectedMediaIds(new Set());
    } else {
      // Select all
      const allAlbumIds = new Set(subAlbums?.map(a => a.id) || []);
      const allMediaIds = new Set(mediaItems?.map(m => m.id) || []);
      setSelectedAlbumIds(allAlbumIds);
      setSelectedMediaIds(allMediaIds);
    }
  };

  const handleBatchMoveComplete = () => {
    // Refresh content
    handleAlbumUpdate();
    setRefreshKey(k => k + 1);
    setSelectedAlbumIds(new Set());
    setSelectedMediaIds(new Set());
  };

  const handleCreateAlbumComplete = () => {
    handleAlbumUpdate();
    setRefreshKey(k => k + 1);
  };

  const handleBatchDelete = async () => {
    if (!firestore || !auth.currentUser) return;

    setIsDeletingBatch(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch('/api/gallery/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          operation: 'delete',
          sourceAlbumIds: Array.from(selectedAlbumIds),
          sourceMediaIds: Array.from(selectedMediaIds)
        })
      });

      if (!response.ok) {
        throw new Error('Batch delete failed');
      }

      toast({
        title: 'Batch Delete Complete',
        description: `Deleted ${totalSelected} items.`
      });

      // Refresh and clear selection
      handleAlbumUpdate();
      setRefreshKey(k => k + 1);
      setSelectedAlbumIds(new Set());
      setSelectedMediaIds(new Set());
      setShowBatchDeleteConfirm(false);
    } catch (error) {
      console.error('Batch delete error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete items.'
      });
    } finally {
      setIsDeletingBatch(false);
    }
  };

  // Fetch album data for edit permissions
  React.useEffect(() => {
    if (!firestore || !albumId) {
      setCurrentAlbum(null);
      return;
    }

    async function fetchAlbumData() {
      try {
        const albumRef = doc(firestore!, 'albums', albumId!);
        const albumSnap = await getDoc(albumRef);
        if (albumSnap.exists()) {
          const data = albumSnap.data();
          setCurrentAlbum({
            id: albumId!,
            name: data.name || 'Untitled Album',
            description: data.description,
            sortName: data.sortName || null,
            createdBy: data.createdBy,
            mediaCount: data.mediaCount || 0,
          });
        }
      } catch (error) {
        console.error('Error fetching album data:', error);
      }
    }

    fetchAlbumData();
  }, [firestore, albumId]);

  // Check if user can edit this album
  const canEditAlbum = React.useMemo(() => {
    if (!currentAlbum || !auth.currentUser) return false;
    if (isAdmin || isOfficer) return true;
    return currentAlbum.createdBy === auth.currentUser.uid;
  }, [currentAlbum, auth.currentUser, isAdmin, isOfficer]);

  // Fetch subalbums and media for Firebase albums
  React.useEffect(() => {
    if (!firestore || !albumId || !albumSource) {
      setHybridSubAlbums([]);
      setHybridMedia([]);
      setHybridLoading(false);
      setHybridError(null);
      return;
    }

    let mounted = true;
    // Capture values to satisfy TypeScript
    const capturedAlbumId = albumId;
    const capturedAlbumSource = albumSource;

    async function fetchAlbumContent() {
      try {
        setHybridLoading(true);
        setHybridError(null);

        // Determine role and Get Token
        let role: 'public' | 'members' = 'public';
        let token: string | undefined;

        if (auth.currentUser) {
          try {
            token = await auth.currentUser.getIdToken();
            role = 'members';
          } catch (e) {
            console.warn("Failed to get ID token, falling back to public cache", e);
            role = 'public'; // Fallback to avoid 401 error
          }
        }

        // 1. Try to load from cache first for immediate display
        const cachedData = await getAlbumCache(firestore!, capturedAlbumId, role);
        if (cachedData && mounted) {
          console.log('Loaded album from cache');
          setHybridSubAlbums(cachedData.subAlbums);
          setHybridMedia(cachedData.media);
          setHybridLoading(false); // Show content immediately
        }

        // 2. Refresh cache via API
        if (mounted) {
          fetch('/api/gallery/sync-album', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              albumId: capturedAlbumId,
              source: capturedAlbumSource,
              role: role
            }),
          })
            .then(async res => {
              // If unauthorized (401/403), and we tried as 'members', retry as 'public'
              if ((res.status === 401 || res.status === 403) && role === 'members') {
                console.warn("Background sync unauthorized, retrying as public");
                const publicRes = await fetch('/api/gallery/sync-album', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    albumId: capturedAlbumId,
                    source: capturedAlbumSource,
                    role: 'public'
                  }),
                });
                if (!publicRes.ok) throw new Error('Public sync failed after Auth failure');
                return publicRes.json();
              }

              if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                throw new Error(errBody.message || 'Sync failed with status: ' + res.status);
              }
              return res.json();
            })
            .then(data => {
              if (mounted) {
                setHybridSubAlbums(data.subAlbums);
                setHybridMedia(parseHybridMediaJson(data.media));
                setHybridLoading(false);
              }
            })
            .catch(e => {
              console.error('Background sync failed:', e);
              // Silent failure as we might be showing cached data
              if (mounted) {
                // Only verify if we have no content
                if (!cachedData) {
                  setHybridError(e instanceof Error ? e : new Error('Failed to load content'));
                  setHybridLoading(false);
                }
              }
            });
        }
      } catch (err) {
        console.error('Error loading album content:', err);
        if (mounted) {
          setHybridError(err instanceof Error ? err : new Error('Failed to load album content'));
        }
      }
    }

    fetchAlbumContent();

    return () => {
      mounted = false;
    };
    return () => {
      mounted = false;
    };
  }, [firestore, albumId, albumSource, refreshKey]);

  // Fetch album path if albumId provided (for Firebase)
  React.useEffect(() => {
    if (!firestore || !albumId) {
      setAlbumPath(null);
      return;
    }

    const fetchAlbumPath = async () => {
      try {
        const albumDoc = await import('firebase/firestore').then(m =>
          m.getDoc(m.doc(firestore, 'albums', albumId))
        );
        if (albumDoc.exists()) {
          // Normalize path to handle legacy paths with leading slashes
          const rawPath = albumDoc.data().albumPath;
          setAlbumPath(normalizeAlbumPath(rawPath));
        }
      } catch (error) {
        console.error('Error fetching album path:', error);
      }
    };

    fetchAlbumPath();
  }, [firestore, albumId, albumSource]);

  // Build query for Firebase albums
  const mediaQuery = React.useMemo(() => {
    if (!firestore) return null;

    let q: Query<DocumentData> = collection(firestore, 'media');

    // Apply filters
    if (albumId && albumPath) {
      // Query by album path to include sub-albums
      q = query(q, where('albumPath', '>=', albumPath));
      q = query(q, where('albumPath', '<', albumPath + '\uf8ff'));
    }

    if (mediaType) {
      q = query(q, where('type', '==', mediaType));
    }

    if (uploadedBy) {
      q = query(q, where('uploadedBy', '==', uploadedBy));
    }

    if (dateRange) {
      q = query(q, where('uploadedAt', '>=', Timestamp.fromDate(dateRange.start)));
      q = query(q, where('uploadedAt', '<=', Timestamp.fromDate(dateRange.end)));
    }

    if (tags && tags.length > 0) {
      // Firestore array-contains only supports single value
      q = query(q, where('tags', 'array-contains', tags[0]));
    }

    // Apply sorting
    const sortField = sortBy === 'newest' || sortBy === 'oldest' ? 'uploadedAt' :
      sortBy === 'popular' ? 'viewCount' : 'title';
    const sortDirection = sortBy === 'oldest' || sortBy === 'title' ? 'asc' : 'desc';

    q = query(q, orderBy(sortField, sortDirection));
    q = query(q, limit(pageSize));

    return q;
  }, [firestore, albumId, albumPath, mediaType, uploadedBy, dateRange, tags, sortBy, pageSize, albumSource]);

  const { data: firebaseMedia, loading: firebaseLoading, error: firebaseError } = useCollection<MediaItem>(
    mediaQuery as Query<MediaItem, DocumentData> | null
  );

  // Determine which data source to use
  const rawMediaItems = (albumId && albumSource) ? hybridMedia : (firebaseMedia as any[] || []);
  const loading = (albumId && albumSource) ? hybridLoading : firebaseLoading;
  const error = (albumId && albumSource) ? hybridError : firebaseError;

  // Apply aircraft model filter if specified
  const mediaItems = React.useMemo(() => {
    if (!aircraftModelFilter || !rawMediaItems) return rawMediaItems;

    const filterLower = aircraftModelFilter.toLowerCase();
    return rawMediaItems.filter(item =>
      item.aircraftModelNames?.some((name: string) =>
        name.toLowerCase().includes(filterLower)
      )
    );
  }, [rawMediaItems, aircraftModelFilter]);

  // Sort subAlbums based on current sortBy selection
  const subAlbums = React.useMemo(() => {
    if (!hybridSubAlbums || hybridSubAlbums.length === 0) return hybridSubAlbums;

    const sorted = [...hybridSubAlbums];
    sorted.sort((a, b) => {
      // For title sorting, use sortName if available (for custom ordering like YYYYMMDD dates)
      if (sortBy === 'title') {
        const sortNameA = a.sortName;
        const sortNameB = b.sortName;
        if (sortNameA && sortNameB) {
          return sortNameA.localeCompare(sortNameB);
        }
        if (sortNameA && !sortNameB) return -1;
        if (!sortNameA && sortNameB) return 1;
        return a.name.localeCompare(b.name);
      }
      // Default: keep server order (sortName -> sortOrder -> name)
      return 0;
    });
    return sorted;
  }, [hybridSubAlbums, sortBy]);

  const handleMediaClick = (index: number) => {
    if (!disableLightbox) {
      setSelectedMediaIndex(index);
    }
    if (onMediaClick && mediaItems) {
      onMediaClick(mediaItems[index]);
    }
  };

  const handleClose = () => {
    setSelectedMediaIndex(null);
  };

  const handleNext = () => {
    if (selectedMediaIndex !== null && mediaItems && mediaItems.length > 0) {
      setSelectedMediaIndex((selectedMediaIndex + 1) % mediaItems.length);
    }
  };

  const handlePrev = () => {
    if (selectedMediaIndex !== null && mediaItems && mediaItems.length > 0) {
      setSelectedMediaIndex((selectedMediaIndex - 1 + mediaItems.length) % mediaItems.length);
    }
  };

  const selectedMedia = selectedMediaIndex !== null && mediaItems && mediaItems[selectedMediaIndex] ? mediaItems[selectedMediaIndex] : null;

  // Notify parent when lightbox media changes (for URL updates)
  React.useEffect(() => {
    onLightboxMediaChange?.(selectedMedia?.id ?? null);
  }, [selectedMedia?.id, onLightboxMediaChange]);

  // Auto-open lightbox when initialMediaId is set and media is loaded
  const [hasAutoOpened, setHasAutoOpened] = React.useState(false);
  React.useEffect(() => {
    if (hasAutoOpened || !initialMediaId || !mediaItems || mediaItems.length === 0) return;
    const index = mediaItems.findIndex(m => m.id === initialMediaId);
    if (index !== -1) {
      setSelectedMediaIndex(index);
      setHasAutoOpened(true);
    }
  }, [initialMediaId, mediaItems, hasAutoOpened]);

  // Lightbox auto-hide controls on mouse idle
  const [lightboxControlsVisible, setLightboxControlsVisible] = React.useState(true);
  const hideTimerRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const resetHideTimer = React.useCallback(() => {
    setLightboxControlsVisible(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setLightboxControlsVisible(false), 3000);
  }, []);

  // Start/reset timer when lightbox opens, clear when it closes
  React.useEffect(() => {
    if (selectedMedia) {
      resetHideTimer();
    } else {
      setLightboxControlsVisible(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    }
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [selectedMedia, resetHideTimer]);

  // Share current image URL
  const handleShareImage = React.useCallback(async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: selectedMedia?.title || 'Gallery Image', url });
      } catch {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied', description: 'Image link copied to clipboard' });
    }
  }, [selectedMedia, toast]);

  // Loading state
  if (loading) {
    return <GalleryBrowserSkeleton viewMode={viewMode} />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-destructive">
        <p className="text-lg font-semibold">Error loading media</p>
        <p className="text-sm text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  // Empty state - only show if no subalbums AND no media
  if ((!subAlbums || subAlbums.length === 0) && (!mediaItems || mediaItems.length === 0)) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <LayoutGrid className="w-12 h-12 mb-4 opacity-50" />
          <p className="text-lg font-semibold">No content found</p>
          <p className="text-sm mb-4">Try adjusting your filters or upload some media</p>
          {canEditAlbum && currentAlbum && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAlbumEdit(true)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Edit Album
            </Button>
          )}
        </div>
        {currentAlbum && (
          <EditAlbumDialog
            open={showAlbumEdit}
            onClose={() => setShowAlbumEdit(false)}
            album={currentAlbum}
            onUpdate={handleAlbumUpdate}
            onDelete={handleAlbumDelete}
          />
        )}
      </>
    );
  }

  return (
    <>
      {/* View mode selector */}
      {showViewModeSelector && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid3x3 className="w-4 h-4 mr-2" />
              Grid
            </Button>
            <Button
              variant={viewMode === 'masonry' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('masonry')}
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Masonry
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4 mr-2" />
              List
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Sort: {sortBy === 'newest' ? 'Newest' : sortBy === 'oldest' ? 'Oldest' : sortBy === 'popular' ? 'Popular' : 'Title'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy('newest')}>Newest First</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('oldest')}>Oldest First</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('popular')}>Most Popular</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('title')}>Title A-Z</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {canEditAlbum && currentAlbum && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAlbumEdit(true)}
              >
                <Settings className="w-4 h-4 mr-2" />
                Edit Album
              </Button>
            )}

            {(isAdmin || isOfficer) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateAlbumDialog(true)}
              >
                <Folder className="w-4 h-4 mr-2" />
                New Album
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Batch Action Bar */}
      {isSelectionActive && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background border shadow-lg rounded-full px-6 py-2 flex items-center gap-4 animate-in slide-in-from-bottom-5">
          <span className="text-sm font-medium">{totalSelected} selected</span>
          <div className="h-4 w-px bg-border" />

          <Button size="sm" variant="ghost" onClick={handleSelectAll}>
            Select All
          </Button>

          <Button
            size="sm"
            onClick={() => setShowBatchMoveDialog(true)}
            disabled={totalSelected === 0}
          >
            <Move className="w-4 h-4 mr-2" />
            Move
          </Button>

          {/* Delete button */}
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setShowBatchDeleteConfirm(true)}
            disabled={totalSelected === 0}
          >
            <Trash className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      )}

      {/* Batch Delete Confirmation */}
      <AlertDialog open={showBatchDeleteConfirm} onOpenChange={setShowBatchDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {totalSelected} Items?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedAlbumIds.size} albums and {selectedMediaIds.size} media items?
              {selectedAlbumIds.size > 0 && " Deleting an album will also delete all its contents."}
              <br /><br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingBatch}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleBatchDelete(); }}
              disabled={isDeletingBatch}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingBatch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash className="w-4 h-4 mr-2" />}
              Confirm Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {currentAlbum && (
        <CreateAlbumDialog
          open={showCreateAlbumDialog}
          onClose={() => setShowCreateAlbumDialog(false)}
          parentId={currentAlbum.id}
          parentPath={albumPath}
          onComplete={handleCreateAlbumComplete}
        />
      )}

      <BatchMoveDialog
        open={showBatchMoveDialog}
        onClose={() => setShowBatchMoveDialog(false)}
        sourceAlbumIds={Array.from(selectedAlbumIds)}
        sourceMediaIds={Array.from(selectedMediaIds)}
        onComplete={handleBatchMoveComplete}
      />

      {/* Subalbums - show as cards before media */}
      {subAlbums && subAlbums.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Albums</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {subAlbums.map((album) => (
              <AlbumCard
                key={album.id}
                album={album}
                onClick={() => {
                  if (isSelectionActive && !selectedAlbumIds.has(album.id)) {
                    // If selection is active and clicking an unselected item, simple click typically opens it?
                    // User said "Instead of a select albums button just have the checkbox appear if we hover".
                    // "Once one is selected we should have a select all button appear".
                    // So standard behavior: Click opens, Checkbox (corner) selects.
                    onAlbumClick?.(album);
                  } else if (isSelectionActive && selectedAlbumIds.has(album.id)) {
                    // If already selected, maybe toggle? Or separate logic?
                    // Let's stick to: Card Click -> Open. Checkbox Click -> Select.
                    onAlbumClick?.(album);
                  } else {
                    onAlbumClick?.(album);
                  }
                }}
                isSelectionActive={isSelectionActive}
                canSelect={isAdmin || isOfficer}
                isSelected={selectedAlbumIds.has(album.id)}
                onToggleSelect={() => toggleAlbumSelection(album.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Media grid/masonry/list */}
      {mediaItems && mediaItems.length > 0 && (
        <>
          {subAlbums && subAlbums.length > 0 && (
            <h3 className="text-lg font-semibold mb-4 mt-8">Media</h3>
          )}

          {/* Drop Preview Banner */}
          <AnimatePresence>
            {isDraggingOver && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 w-full"
              >
                <Card className="border-2 border-dashed border-primary bg-primary/5 text-primary">
                  <CardContent className="flex flex-col items-center justify-center h-32">
                    <Upload className="w-10 h-10 mb-2 animate-bounce" />
                    <p className="text-lg font-bold">Drop files to upload</p>
                    <p className="text-sm opacity-80">
                      {currentAlbum ? `Adding to ${currentAlbum.name}` : 'Select an album after drop'}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {viewMode === 'grid' && (
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {mediaItems.map((media, index) => (
                <motion.div key={media.id} variants={itemVariants}>
                  <MediaCard
                    media={media}
                    onClick={() => handleMediaClick(index)}
                    viewMode="grid"
                    canManage={isAdmin || isOfficer /* Only admins can select for now, or match AlbumCard logic */}
                    isSelectionActive={isSelectionActive}
                    canSelect={isAdmin || isOfficer}
                    isSelected={selectedMediaIds.has(media.id)}
                    onToggleSelect={() => toggleMediaSelection(media.id)}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}

          {viewMode === 'masonry' && (
            <motion.div
              className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {mediaItems.map((media, index) => (
                <motion.div key={media.id} variants={itemVariants} className="break-inside-avoid mb-4">
                  <MediaCard
                    media={media}
                    onClick={() => handleMediaClick(index)}
                    viewMode="masonry"
                    canManage={canManage}
                    onEdit={handleEditMedia}
                    onDelete={handleDeleteClick}
                    onMove={handleMoveClick}
                    onCopy={handleCopyClick}
                    onTagAircraft={handleTagAircraftClick}
                    isSelectionActive={isSelectionActive}
                    canSelect={isAdmin || isOfficer}
                    isSelected={selectedMediaIds.has(media.id)}
                    onToggleSelect={() => toggleMediaSelection(media.id)}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}

          {viewMode === 'list' && (
            <motion.div
              className="flex flex-col gap-2"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {mediaItems.map((media, index) => (
                <motion.div key={media.id} variants={itemVariants}>
                  <MediaCard
                    media={media}
                    onClick={() => handleMediaClick(index)}
                    viewMode="list"
                    canManage={canManage}
                    onEdit={handleEditMedia}
                    onDelete={handleDeleteClick}
                    onMove={handleMoveClick}
                    onCopy={handleCopyClick}
                    onTagAircraft={handleTagAircraftClick}
                    isSelectionActive={isSelectionActive}
                    canSelect={isAdmin || isOfficer}
                    isSelected={selectedMediaIds.has(media.id)}
                    onToggleSelect={() => toggleMediaSelection(media.id)}
                  />
                </motion.div>
              ))}
            </motion.div>
          )}
        </>
      )}

      {/* Lightbox dialog */}
      <Dialog open={!!selectedMedia} onOpenChange={(isOpen) => !isOpen && handleClose()}>
        <DialogContent
          className="max-w-none w-screen h-screen bg-black border-0 p-0 flex items-center justify-center [&>button:last-of-type]:hidden"
          onMouseMove={resetHideTimer}
          style={{
            cursor: lightboxControlsVisible ? undefined : 'none',
            // Override default dialog zoom/slide animation — use fade-only for full-screen lightbox
            // to prevent the underlying page from being briefly revealed at the edges
            '--tw-enter-scale': '1',
            '--tw-exit-scale': '1',
            '--tw-enter-translate-y': '-50%',
            '--tw-exit-translate-y': '-50%',
          } as React.CSSProperties}
        >
          <VisuallyHidden>
            <DialogTitle>{selectedMedia?.title || 'Media Viewer'}</DialogTitle>
          </VisuallyHidden>
          {selectedMedia && (
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Prev button */}
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "absolute left-2 top-1/2 -translate-y-1/2 z-10 text-white bg-black/30 hover:bg-black/50 h-10 w-10 transition-opacity duration-300",
                  lightboxControlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                onClick={handlePrev}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>

              {/* Media content — full space */}
              <div className="relative w-full h-full" ref={lightboxContainerRef}>
                {selectedMedia.type === 'photo' && (selectedMedia as any).isPanorama ? (
                  <PanoramaViewer
                    imageUrl={imageCacheBuster ? `${selectedMedia.url}?v=${imageCacheBuster}` : selectedMedia.url}
                  />
                ) : selectedMedia.type === 'photo' ? (
                  <Image
                    src={imageCacheBuster ? `${selectedMedia.url}?v=${imageCacheBuster}` : selectedMedia.url}
                    alt={selectedMedia.title}
                    fill
                    className="object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-full max-w-5xl aspect-video">
                      <iframe
                        width="100%"
                        height="100%"
                        src={getVideoEmbedUrl(selectedMedia as MediaItem)}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        title={selectedMedia.title}
                        className="rounded-lg"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Next button */}
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "absolute right-2 top-1/2 -translate-y-1/2 z-10 text-white bg-black/30 hover:bg-black/50 h-10 w-10 transition-opacity duration-300",
                  lightboxControlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
                onClick={handleNext}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>

              {/* Top-right controls */}
              <div className={cn(
                "absolute top-4 right-4 z-10 flex items-center gap-2 transition-opacity duration-300",
                lightboxControlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
              )}>
                {/* Share button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white bg-black/30 hover:bg-black/50 h-10 w-10"
                  onClick={handleShareImage}
                  title="Share link"
                >
                  <Share2 className="h-5 w-5" />
                </Button>
                {/* Fullscreen button */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white bg-black/30 hover:bg-black/50 h-10 w-10"
                  onClick={togglePanoFullscreen}
                  title={isPanoFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                >
                  {isPanoFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
                </Button>
                {/* Rotate buttons — visible to admins/officers or image owner, hidden for panoramas */}
                {selectedMedia.type === 'photo' && !(selectedMedia as any).isPanorama && (isAdmin || isOfficer || ('uploadedBy' in selectedMedia && (selectedMedia as any).uploadedBy === auth.currentUser?.uid)) && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white bg-black/30 hover:bg-black/50 h-10 w-10"
                      onClick={() => handleRotateImage('ccw')}
                      disabled={isRotating}
                      title="Rotate counter-clockwise"
                    >
                      {isRotating ? <Loader2 className="h-5 w-5 animate-spin" /> : <RotateCcw className="h-5 w-5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white bg-black/30 hover:bg-black/50 h-10 w-10"
                      onClick={() => handleRotateImage('cw')}
                      disabled={isRotating}
                      title="Rotate clockwise"
                    >
                      {isRotating ? <Loader2 className="h-5 w-5 animate-spin" /> : <RotateCw className="h-5 w-5" />}
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white bg-black/30 hover:bg-black/50 h-10 w-10"
                  onClick={handleClose}
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Bottom metadata overlay */}
              <div className={cn(
                "absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white p-4 rounded-md max-w-2xl transition-opacity duration-300",
                lightboxControlsVisible ? "opacity-100" : "opacity-0 pointer-events-none"
              )}>
                <p className="text-sm font-semibold mb-1">
                  {selectedMedia.title}
                  {(selectedMedia as any).isPanorama && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-white/20 align-middle">360</span>
                  )}
                </p>
                {selectedMedia.description && (
                  <p className="text-xs text-gray-300 mb-2">{selectedMedia.description}</p>
                )}
                <div className="flex items-center gap-4 text-xs text-gray-300">
                  <UploaderName
                    name={selectedMedia.uploaderName}
                    userId={'uploadedBy' in selectedMedia ? (selectedMedia as any).uploadedBy : undefined}
                    showUserIcon
                  />
                  {selectedMedia.uploadedAt && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDistanceToNow(
                        selectedMedia.uploadedAt instanceof Date
                          ? selectedMedia.uploadedAt
                          : selectedMedia.uploadedAt.toDate(),
                        { addSuffix: true }
                      )}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Eye className="w-3 h-3" />
                    {selectedMedia.viewCount || 0} views
                  </span>
                </div>
                {/* Aircraft Model Badges */}
                {selectedMedia.aircraftModelNames && selectedMedia.aircraftModelNames.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedMedia.aircraftModelNames.map((name: string, index: number) => {
                      const modelId = selectedMedia.aircraftModelIds?.[index];
                      return (
                        <a
                          key={modelId || name}
                          href={`/flying?model=${encodeURIComponent(name)}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30 text-xs transition-colors"
                        >
                          <Plane className="w-3 h-3" />
                          {name}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {mediaToEdit && (
        <EditMediaDialog
          open={!!mediaToEdit}
          onClose={() => setMediaToEdit(null)}
          media={mediaToEdit}
          onUpdate={handleMediaUpdate}
        />
      )}

      <AlertDialog open={isDeleting || !!mediaToDelete} onOpenChange={(open) => !open && setMediaToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the media item
              from the gallery.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash className="mr-2 h-4 w-4" />}
              Delete Item
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {currentAlbum && (
        <EditAlbumDialog
          open={showAlbumEdit}
          onClose={() => setShowAlbumEdit(false)}
          album={currentAlbum}
          onUpdate={handleAlbumUpdate}
          onDelete={handleAlbumDelete}
        />
      )}

      {mediaToMove && (
        <MoveCopyMediaDialog
          open={!!mediaToMove}
          onClose={() => setMediaToMove(null)}
          media={mediaToMove}
          mode={moveCopyMode}
          onComplete={handleMoveCopyComplete}
        />
      )}

      {mediaToTagAircraft && (
        <QuickTagAircraftDialog
          open={!!mediaToTagAircraft}
          onClose={() => setMediaToTagAircraft(null)}
          media={mediaToTagAircraft}
          onUpdate={handleMediaUpdate}
        />
      )}
    </>
  );
}

/**
 * Component to display uploader name, resolving from email to display name if needed
 */
function UploaderName({ name, userId, showUserIcon }: { name?: string, userId?: string, showUserIcon?: boolean }) {
  const [displayName, setDisplayName] = React.useState(name);
  const firestore = useFirestore();

  React.useEffect(() => {
    if (!firestore) return;

    const resolveAndFetch = async () => {
      let currentUserId = userId;

      // If no userId but name looks like email, try to find user by email
      if (!currentUserId && name && name.includes('@')) {
        try {
          const usersRef = collection(firestore, 'users');
          const q = query(usersRef, where('email', '==', name));
          const snap = await getDocs(q);
          if (!snap.empty) {
            currentUserId = snap.docs[0].id;
          }
        } catch (e) {
          // Ignore error
        }
      }

      // If we still don't have a userId, we can't do profile lookup
      if (!currentUserId) {
        setDisplayName(name);
        return;
      }

      try {
        // 1. Try to find a membership linked to this user
        const membershipsRef = collection(firestore, 'memberships');
        const q = query(membershipsRef, where('linkedUserId', '==', currentUserId));
        const membershipSnap = await getDocs(q);

        if (!membershipSnap.empty) {
          const membershipData = membershipSnap.docs[0].data();
          if (membershipData.profileId) {
            const profileDoc = await getDoc(doc(firestore, 'profiles', membershipData.profileId));
            if (profileDoc.exists()) {
              const profile = profileDoc.data();
              if (profile.firstName && profile.lastName) {
                setDisplayName(`${profile.firstName} ${profile.lastName}`);
                return;
              }
            }
          }
        }

        // 2. Fallback: Try User document directly
        const userDoc = await getDoc(doc(firestore, 'users', currentUserId));
        if (userDoc.exists()) {
          const data = userDoc.data();

          // Check if user doc has direct profileId link
          if (data.profileId) {
            const profileDoc = await getDoc(doc(firestore, 'profiles', data.profileId));
            if (profileDoc.exists()) {
              const profile = profileDoc.data();
              if (profile.firstName && profile.lastName) {
                setDisplayName(`${profile.firstName} ${profile.lastName}`);
                return;
              }
            }
          }

          // Fallback to user displayName
          const newName = data.displayName || (data.firstName && data.lastName ? `${data.firstName} ${data.lastName}` : '');
          if (newName) {
            setDisplayName(newName);
            return;
          }
        }
      } catch (e) {
        // Ignore errors
      }
    };

    resolveAndFetch();
  }, [name, userId, firestore]);

  // If it's still an email, hide it completely as requested
  if (displayName?.includes('@')) return null;

  if (showUserIcon) {
    return (
      <span className="flex items-center gap-1">
        <User className="w-3 h-3" />
        {displayName}
      </span>
    );
  }

  return <>{displayName}</>;
}

/**
 * Individual media card for grid/masonry/list views
 */
function MediaCard({
  media,
  onClick,
  viewMode,
  canManage,
  onEdit,
  onDelete,
  onMove,
  onCopy,
  onTagAircraft,
  isSelectionActive,
  canSelect,
  isSelected,
  onToggleSelect
}: {
  media: MediaItem | HybridMediaItem;
  onClick: () => void;
  viewMode: ViewMode;
  canManage?: boolean;
  onEdit?: (media: MediaItem | HybridMediaItem) => void;
  onDelete?: (media: MediaItem | HybridMediaItem) => void;
  onMove?: (media: MediaItem | HybridMediaItem) => void;
  onCopy?: (media: MediaItem | HybridMediaItem) => void;
  onTagAircraft?: (media: MediaItem | HybridMediaItem) => void;
  isSelectionActive?: boolean;
  canSelect?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}) {
  // Helper to get uploadedAt as Date
  const getUploadDate = (): Date | undefined => {
    if (!media.uploadedAt) return undefined;
    if (media.uploadedAt instanceof Date) return media.uploadedAt;
    if ('toDate' in media.uploadedAt) return media.uploadedAt.toDate();
    return undefined;
  };

  const uploadDate = getUploadDate();

  if (viewMode === 'list') {
    return (
      <Card
        className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
        onClick={onClick}
      >
        <div className="flex items-center gap-4 p-4">
          <div className="relative w-24 h-24 flex-shrink-0 rounded overflow-hidden bg-muted">
            {media.type === 'photo' ? (
              <Image
                src={media.thumbnailUrl}
                alt={media.title}
                fill
                className="object-cover"
              />
            ) : (
              <DriveThumbnail
                media={media as MediaItem}
                alt={media.title}
                fill
                className="object-cover"
                showPlayIcon={true}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{media.title}</h3>
            {media.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">{media.description}</p>
            )}
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <UploaderName name={media.uploaderName} userId={'uploadedBy' in media ? (media as any).uploadedBy : undefined} />
              {uploadDate && (
                <span>{formatDistanceToNow(uploadDate, { addSuffix: true })}</span>
              )}
              <span>{media.viewCount || 0} views</span>
            </div>
          </div>
          {media.tags && media.tags.length > 0 && (
            <div className="flex gap-1 flex-wrap max-w-xs">
              {media.tags.slice(0, 3).map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </Card>
    );
  }

  // Grid and masonry views
  return (
    <div
      className={`overflow-hidden rounded-lg ${viewMode === 'masonry' ? 'break-inside-avoid' : 'aspect-square'} group relative hover:shadow-lg transition-all ${isSelected ? 'ring-2 ring-primary' : ''}`}
    >
      <div className="relative w-full h-full cursor-pointer" onClick={onClick}>
        {canSelect && (
          <div
            className={cn("absolute top-2 left-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity", isSelected && "opacity-100", isSelectionActive && "opacity-100")}
            onClick={(e) => { e.stopPropagation(); }}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect?.()}
              className="bg-white/90 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground border-white/50"
            />
          </div>
        )}
        {media.type === 'photo' ? (
          viewMode === 'grid' ? (
            <Image
              src={media.thumbnailUrl}
              alt={media.title}
              fill
              className="object-cover transform transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div
              className="relative w-full"
              style={{ aspectRatio: media.width && media.height ? `${media.width}/${media.height}` : '4/3' }}
            >
              <Image
                src={media.thumbnailUrl}
                alt={media.title}
                fill
                className="object-cover transform transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          )
        ) : (
          <div className="relative w-full aspect-video">
            <DriveThumbnail
              media={media as MediaItem}
              alt={media.title}
              fill
              className="object-cover transform transition-transform duration-300 group-hover:scale-105"
              showPlayIcon={true}
            />
          </div>
        )}
        {/* 360 panorama badge */}
        {(media as any).isPanorama && (
          <div className="absolute top-2 right-2 z-10 pointer-events-none">
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] font-bold backdrop-blur-sm">360</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
            <p className="text-sm font-semibold truncate">{media.title}</p>
            {media.uploaderName && (
              <p className="text-xs opacity-90">
                <UploaderName
                  name={media.uploaderName}
                  userId={'uploadedBy' in media ? (media as any).uploadedBy : undefined}
                />
              </p>
            )}
          </div>
        </div>
      </div>

      {canManage && (!('source' in media) || (media as any).source === 'firebase') && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="icon" className="h-8 w-8 bg-black/50 hover:bg-black/70 text-white border-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                // Delay to let dropdown close before opening dialog (prevents focus conflict)
                setTimeout(() => onEdit && onEdit(media), 0);
              }}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setTimeout(() => onMove && onMove(media), 0);
              }}>
                <Move className="mr-2 h-4 w-4" />
                Move to Album
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setTimeout(() => onCopy && onCopy(media), 0);
              }}>
                <Copy className="mr-2 h-4 w-4" />
                Copy to Album
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                setTimeout(() => onTagAircraft && onTagAircraft(media), 0);
              }}>
                <Plane className="mr-2 h-4 w-4" />
                Tag Aircraft
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  // Delay to let dropdown close before opening dialog (prevents focus conflict)
                  setTimeout(() => onDelete && onDelete(media), 0);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}

/**
 * Album card for displaying subalbums
 */
function AlbumCard({
  album,
  onClick,
  isSelectionActive,
  canSelect,
  isSelected,
  onToggleSelect
}: {
  album: HybridAlbum;
  onClick: () => void;
  isSelectionActive?: boolean;
  canSelect?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}) {
  return (
    <Card
      className={cn(
        "cursor-pointer hover:shadow-lg transition-shadow overflow-hidden relative group",
        isSelected && "ring-2 ring-primary"
      )}
      onClick={onClick}
    >
      {canSelect && (
        <div
          className={cn("absolute top-2 left-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity", isSelected && "opacity-100", isSelectionActive && "opacity-100")}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggleSelect?.()}
            className="bg-white/90 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground border-white/50"
          />
        </div>
      )}

      {album.coverUrl ? (
        <div className="relative aspect-video overflow-hidden">
          <Image
            src={album.coverUrl}
            alt={album.name}
            fill
            className="object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
        </div>
      ) : (
        <div className="relative aspect-video bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
          <Folder className="w-12 h-12 text-muted-foreground/50" />
        </div>
      )}
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Folder className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{album.name}</span>
        </CardTitle>
        {album.description && (
          <CardDescription className="text-xs line-clamp-2">
            {album.description}
          </CardDescription>
        )}
        {album.mediaCount > 0 && (
          <CardDescription className="text-xs">
            {album.mediaCount} {album.mediaCount === 1 ? 'item' : 'items'}
          </CardDescription>
        )}
      </CardHeader>
    </Card>
  );
}

/**
 * Loading skeleton for gallery browser
 */
function GalleryBrowserSkeleton({ viewMode }: { viewMode: ViewMode }) {
  const count = viewMode === 'list' ? 5 : 12;

  if (viewMode === 'list') {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: count }).map((_, i) => (
          <Card key={i} className="p-4">
            <div className="flex items-center gap-4">
              <Skeleton className="w-24 h-24 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4' : 'columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4'}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton
          key={i}
          className={viewMode === 'grid' ? 'aspect-square' : `h-${[48, 64, 56, 72][i % 4]}`}
        />
      ))}
    </div>
  );
}
